import { z } from 'zod';
import type { SupabaseClient } from '../supabase/client';
import type { Json } from '../supabase/database.types';
import type {
  InventoryUpdateErrorKey,
  StockErrorKey,
  ListStockWorklistInput,
  StockWorklistCursor,
  StockWorklistErrorCode,
  StockWorklistErrorKey,
  StockWorklistPage,
  UpdateVariantInventoryInput,
} from '../types/stock';
import { LOW_STOCK_THRESHOLD } from '../lib/constants';

const uuid = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i);
const count = z.number().int().nonnegative().max(Number.MAX_SAFE_INTEGER);
const inventory = count.max(2147483647);
const scope = z.enum(['active', 'all']);
const filter = z.enum(['all', 'out_of_stock', 'low_stock']);
const query = z.string().refine((value) => [...value].length <= 100);
const cursorSchema = z
  .object({
    v: z.literal(1),
    s: uuid,
    q: query,
    c: scope,
    f: filter,
    a: inventory,
    i: uuid,
  })
  .strict();
const inputSchema = z.object({
  storeId: uuid,
  scope,
  filter,
  query,
  cursor: cursorSchema.nullish(),
  limit: z.number().int().min(1).max(100).default(50),
});
const rowSchema = z
  .object({
    storeId: uuid,
    variantId: uuid,
    productId: uuid,
    productName: z.string().refine((value) => [...value].length >= 1 && [...value].length <= 200),
    optionSummary: z.string().refine((value) => [...value].length <= 512),
    barcode: z
      .string()
      .refine((value) => [...value].length <= 80)
      .nullable(),
    active: z.boolean(),
    productPublished: z.boolean(),
    availableUnits: inventory,
    status: z.enum(['healthy', 'low_stock', 'out_of_stock']),
    primaryImagePath: z.string().nullish(),
  })
  .strict();
const pageSchema = z
  .object({
    version: z.literal(1),
    storeId: uuid,
    scope,
    filter,
    query,
    observedAt: z
      .string()
      .datetime({ offset: true })
      .refine((value) => Number.isFinite(Date.parse(value))),
    catalogVariantCount: count,
    scopeCounts: z.object({ variants: count, outOfStock: count, lowStock: count }).strict(),
    matchingCount: count,
    items: z.array(rowSchema),
    nextCursor: cursorSchema.nullable(),
  })
  .strict();

export class StockWorklistError extends Error {
  readonly code: StockWorklistErrorCode;

  constructor(code: StockWorklistErrorCode) {
    super(code);
    this.name = 'StockWorklistError';
    this.code = code;
  }
}

export function stockWorklistErrorKey(error: unknown): StockWorklistErrorKey {
  if (error instanceof StockWorklistError) {
    if (error.code === 'access_denied') return 'admin.stock.worklist.accessDenied';
    if (error.code === 'invalid_request') return 'admin.stock.worklist.invalidQuery';
  }
  return 'admin.stock.worklist.loadFailed';
}

function sameContext(cursor: StockWorklistCursor, input: ListStockWorklistInput): boolean {
  return (
    cursor.s === input.storeId &&
    cursor.c === input.scope &&
    cursor.f === input.filter &&
    cursor.q === input.query
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function mapWorklistError(error: unknown, status?: number): StockWorklistError {
  if (error instanceof StockWorklistError) return error;
  const code = isRecord(error) ? error.code : undefined;
  const httpStatus = status ?? (isRecord(error) ? error.status : undefined);
  if (code === '42501' || httpStatus === 401 || httpStatus === 403)
    return new StockWorklistError('access_denied');
  if (code === '22023' || code === '22P02' || code === '22003')
    return new StockWorklistError('invalid_request');
  if (
    error instanceof TypeError ||
    httpStatus === 0 ||
    (isRecord(error) &&
      typeof error.message === 'string' &&
      /fetch|network|timeout/i.test(error.message))
  ) {
    return new StockWorklistError('network');
  }
  return new StockWorklistError('unknown');
}

/** Strict, bounded read adapter. A malformed page never becomes partial success. */
export async function listStockWorklist(
  client: SupabaseClient,
  input: ListStockWorklistInput,
  options?: { readonly signal?: AbortSignal },
): Promise<StockWorklistPage> {
  options?.signal?.throwIfAborted();
  const parsed = inputSchema.safeParse({
    ...input,
    query: typeof input.query === 'string' ? input.query.trim() : input.query,
  });
  if (!parsed.success) throw new StockWorklistError('invalid_request');
  const request = parsed.data;
  if (
    request.cursor &&
    (!sameContext(request.cursor, request) ||
      new TextEncoder().encode(JSON.stringify(request.cursor)).length > 1024)
  ) {
    throw new StockWorklistError('invalid_request');
  }
  try {
    const rpc = client.rpc('stock_worklist_page', {
      p_store_id: request.storeId,
      p_scope: request.scope,
      p_status: request.filter,
      p_query: request.query,
      p_cursor: request.cursor ? ({ ...request.cursor } as Json) : undefined,
      p_limit: request.limit,
    });
    const { data, error, status } = await (options?.signal ? rpc.abortSignal(options.signal) : rpc);
    options?.signal?.throwIfAborted();
    if (error) throw mapWorklistError(error, status);
    const result = pageSchema.safeParse(data);
    if (!result.success) throw new StockWorklistError('invalid_response');
    const page = result.data;
    const counts = page.scopeCounts;
    if (
      page.storeId !== request.storeId ||
      page.scope !== request.scope ||
      page.filter !== request.filter ||
      page.query !== request.query ||
      counts.outOfStock + counts.lowStock > counts.variants ||
      counts.variants > page.catalogVariantCount ||
      page.matchingCount > counts.variants ||
      page.items.length > request.limit ||
      page.items.length > page.matchingCount ||
      new Set(page.items.map((row) => row.variantId)).size !== page.items.length
    ) {
      throw new StockWorklistError('invalid_response');
    }
    let previous = request.cursor
      ? { availableUnits: request.cursor.a, variantId: request.cursor.i }
      : null;
    for (const row of page.items) {
      const expected =
        row.availableUnits === 0
          ? 'out_of_stock'
          : row.availableUnits <= LOW_STOCK_THRESHOLD
            ? 'low_stock'
            : 'healthy';
      const path = row.primaryImagePath;
      if (
        row.storeId !== request.storeId ||
        row.status !== expected ||
        (request.scope === 'active' && !row.active) ||
        (request.filter !== 'all' && row.status !== request.filter) ||
        (previous &&
          (row.availableUnits < previous.availableUnits ||
            (row.availableUnits === previous.availableUnits &&
              row.variantId.toLowerCase() <= previous.variantId.toLowerCase()))) ||
        (path != null &&
          (!path.startsWith(request.storeId + '/') ||
            !/^[0-9a-f-]{36}\/[^/]+\.(jpg|png|webp)$/i.test(path) ||
            /[\\?#%]/.test(path) ||
            [...path].some((character) => character.charCodeAt(0) < 32)))
      ) {
        throw new StockWorklistError('invalid_response');
      }
      previous = row;
    }
    const last = page.items.at(-1);
    if (
      page.nextCursor &&
      (!sameContext(page.nextCursor, request) ||
        !last ||
        page.nextCursor.a !== last.availableUnits ||
        page.nextCursor.i !== last.variantId ||
        page.items.length !== request.limit)
    ) {
      throw new StockWorklistError('invalid_response');
    }
    return {
      ...page,
      items: page.items.map((row) => ({
        ...row,
        primaryImagePath: row.primaryImagePath ?? null,
        primaryImageUrl: row.primaryImagePath
          ? client.storage.from('product-images').getPublicUrl(row.primaryImagePath).data.publicUrl
          : null,
      })),
    };
  } catch (error) {
    options?.signal?.throwIfAborted();
    if (isRecord(error) && error.name === 'AbortError') throw error;
    throw mapWorklistError(error);
  }
}

export async function updateVariantInventory(
  client: SupabaseClient,
  input: UpdateVariantInventoryInput,
): Promise<void> {
  const { error } = await client.rpc('update_variant_inventory', {
    target_variant_id: input.variantId,
    new_inventory: input.newInventory,
  });

  if (error) throw error;
}

/**
 * Set a product's absolute inventory count.
 *
 * Uses `.select('id').single()` to ensure the row exists and to surface the
 * RLS check result rather than silently succeeding on a missing row.
 *
 * Throws on Supabase error (including P0001 from the trigger).
 */
export async function updateProductInventory(
  client: SupabaseClient,
  productId: string,
  newInventory: number,
): Promise<void> {
  const { data, error: variantError } = await client
    .from('product_variants')
    .select('id')
    .eq('product_id', productId)
    .eq('is_default', true)
    .is('deleted_at', null)
    .maybeSingle();
  if (variantError) throw variantError;
  if (!data) throw Object.assign(new Error('variant not found'), { code: 'P0002' });

  await updateVariantInventory(client, {
    variantId: (data as { id: string }).id,
    newInventory,
  });
}

/**
 * Legacy error mapper retained for compatibility with existing callers.
 */
export function mapStockError(err: unknown): StockErrorKey {
  if (
    err instanceof TypeError ||
    (err instanceof Error && err.name === 'AuthRetryableFetchError') ||
    (err instanceof Error && err.message.toLowerCase().includes('fetch'))
  ) {
    return 'admin.stock.errors.networkFailed';
  }
  return 'admin.stock.errors.unknown';
}

/**
 * Maps a service-level error thrown by updateProductInventory to a
 * translated i18n key.
 */
export function mapInventoryUpdateError(err: unknown): InventoryUpdateErrorKey {
  if (
    err instanceof TypeError ||
    (err instanceof Error && err.name === 'AuthRetryableFetchError') ||
    (err instanceof Error && err.message.toLowerCase().includes('fetch'))
  ) {
    return 'admin.stock.updateInventory.errors.networkFailed';
  }

  return 'admin.stock.updateInventory.errors.unknown';
}
